import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabase';
import { PASSWORD_MIN_LENGTH, PASSWORD_POLICY_MESSAGE, PASSWORD_PLACEHOLDER, isPasswordValid } from '../lib/passwordPolicy';
import PasswordInput from './PasswordInput';
import './Login.css';

/**
 * Pagina raggiunta dal link "Reimposta la password" nell'email.
 * Supabase-js elabora il token dal fragment dell'URL e apre una sessione di
 * recovery (evento PASSWORD_RECOVERY). Qui: se la sessione di recovery c'è ->
 * form nuova password; altrimenti -> link scaduto/già usato.
 */
function ResetPassword() {
  const navigate = useNavigate();
  const { updatePassword } = useAuth();

  const [phase, setPhase] = useState('checking'); // checking | ready | invalid | done
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let settled = false;
    const markReady = () => { settled = true; setPhase('ready'); };

    // 1) l'evento di recovery può scattare al caricamento del token nell'URL
    const { data: sub } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === 'PASSWORD_RECOVERY' || (session && event === 'SIGNED_IN')) markReady();
    });

    // 2) fallback: se una sessione (di recovery) è già attiva
    supabase.auth.getSession().then(({ data }) => {
      if (data?.session) markReady();
    });

    // 3) se dopo qualche secondo non c'è nessuna sessione di recovery -> link non valido
    const t = setTimeout(() => { if (!settled) setPhase('invalid'); }, 4000);

    return () => { clearTimeout(t); sub?.subscription?.unsubscribe?.(); };
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!isPasswordValid(password)) { setError(PASSWORD_POLICY_MESSAGE); return; }
    if (password !== confirm) { setError('Le due password non coincidono.'); return; }
    setSaving(true);
    try {
      await updatePassword(password);
      setPhase('done');
      setTimeout(() => navigate('/', { replace: true }), 2500);
    } catch (err) {
      setError(err.message?.includes('expired') || err.message?.includes('invalid')
        ? 'Link scaduto o già utilizzato. Richiedi un nuovo reset.'
        : (err.message || 'Errore durante il salvataggio della password.'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="login-container">
      <div className="login-card">
        <h1 className="login-title">Uptra</h1>

        {phase === 'checking' && (
          <p className="login-subtitle">Verifica del link in corso…</p>
        )}

        {phase === 'invalid' && (
          <>
            <p className="login-subtitle">Link scaduto o già utilizzato</p>
            <div className="info-message">
              Il link di reset non è più valido. Richiedine uno nuovo dalla pagina di accesso.
            </div>
            <div className="login-footer">
              <p><Link to="/">Torna all'accesso</Link></p>
            </div>
          </>
        )}

        {phase === 'ready' && (
          <>
            <p className="login-subtitle">Scegli una nuova password</p>
            <form className="login-form" onSubmit={handleSubmit}>
              <div className="form-group">
                <label htmlFor="new-password">Nuova password</label>
                <PasswordInput
                  id="new-password" value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder={PASSWORD_PLACEHOLDER} autoComplete="new-password"
                  minLength={PASSWORD_MIN_LENGTH} required
                />
              </div>
              <div className="form-group">
                <label htmlFor="confirm-password">Conferma password</label>
                <PasswordInput
                  id="confirm-password" value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  placeholder="ripeti la password" autoComplete="new-password"
                  minLength={PASSWORD_MIN_LENGTH} required
                />
              </div>
              {error && <div className="error-message">{error}</div>}
              <button type="submit" className="btn btn-primary" disabled={saving}>
                {saving ? 'Salvataggio…' : 'Imposta nuova password'}
              </button>
            </form>
          </>
        )}

        {phase === 'done' && (
          <>
            <p className="login-subtitle">Password aggiornata ✓</p>
            <div className="info-message">
              La tua password è stata cambiata. Ti reindirizzo all'app…
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default ResetPassword;
